import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { useQueryClient } from "@tanstack/react-query";

import { restaurantCategoryService } from "../../api/useQueryHooks";
import { QUERY_KEYS } from "../../api/QueryKeys";

import Container from "../../components/Container";
import Loader from "../../components/Loader";
import AlertMsg from "../../components/AlertMsg";
import AddRestaurantCategoryModal from "../../components/AddRestaurantCategoryModal";
import AppDialog from "../../components/AppDialog";

import "../../assets/css/custom.css";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete1.svg";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontFamily: "GilroySemiBold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "GilroyMedium",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const Categories = () => {
  const queryClient = useQueryClient();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [appDialogState, setAppDialogState] = useState({ open: false });
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "",
  });

  const categoriesList = restaurantCategoryService.useGetRestaurantCategories();
  const deleteCategoryMutation = restaurantCategoryService.useDeleteRestaurantCategory(() => {
    queryClient.invalidateQueries([QUERY_KEYS.categories]);
    closeAppDialog();
  });
  const createCategoryMutation = restaurantCategoryService.useCreateRestaurantCategory((response) => {
    queryClient.invalidateQueries([QUERY_KEYS.categories]);
    closeModal();
    showSuccessMessage(response.msg);
  }, (response) => {
    closeModal();
    showErrorMessage(response.msg);
  });

  const updateCategoryMutation = restaurantCategoryService.useUpdateRestaurantCategory((response) => {
    queryClient.invalidateQueries([QUERY_KEYS.categories]);
    closeModal();
    showSuccessMessage(response.msg);
  }, (response) => {
    closeModal();
    showErrorMessage(response.msg);
  });

  const onAddEdit = (item) => {
    setItemData(item);
    setAddModalVisible(true);
  };

  const handleSubmit = (item) => {
    if (item._id) updateCategoryMutation.mutate(item);
    else createCategoryMutation.mutate(item);
  }

  const handleDeleteClick = (category) => {
    setAppDialogState({
      open: true,
      title: (
        <Box>
          <Typography sx={{ margin: 0 }}>
            <DeleteIcon width={"2.5rem"} height={"2.5rem"} />
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}
          >
            Delete Category?
          </Typography>
        </Box>
      ),
      content: (
        <>
          Are you sure you want to delete the category "{category.name}"? This action
          cannot be undone.
        </>
      ),
      firstBtnText: "Cancel",
      secondBtnText: "Delete",
      onClose: closeAppDialog,
      onConfirm: () => {
        deleteCategoryMutation.mutate(category._id);
      },
    });
  };

  const closeAppDialog = () => setAppDialogState({ ...appDialogState, open: false });

  const closeModal = () => {
    setAddModalVisible(false);
    setItemData(null);
  };

  const showSuccessMessage = (msg) => {
    setMsgAlert({
      open: true,
      message: msg,
      msgType: "success",
    });
  };

  const showErrorMessage = (msg) => {
    setMsgAlert({
      open: true,
      message: msg,
      msgType: "error",
    })
  }

  return (
    <Container
      page={"categories"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      {categoriesList.isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant={"h6"}
              sx={{
                fontFamily: "GilroyBold",
              }}
            >
              Restaurant Categories
            </Typography>

            <Button
              type="button"
              sx={{
                backgroundColor: "#000",
                color: "#FFF",
                width: 100,
                fontFamily: "GilroyBold",
                fontSize: 14,
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#000 !important",
                },
              }}
              onClick={() => onAddEdit(null)}
            >
              + ADD
            </Button>
          </Box>

          <Grid container direction="row" mt={2}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>Category</StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(categoriesList.data?.rows ?? []).map((row, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ width: 40 }}>
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <DeleteIcon
                          width={24}
                          height={24}
                          style={{
                            cursor: "pointer",
                            marginRight: '1rem'
                          }}
                          onClick={() => handleDeleteClick(row)}
                        />
                        <EditIcon
                          height={24}
                          width={24}
                          style={{ cursor: "pointer" }}
                          onClick={() => onAddEdit(row)}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      )}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() =>
          setMsgAlert({ open: false, message: "", msgType: "error" })
        }
      />
      <AddRestaurantCategoryModal
        open={addModalVisible}
        itemData={itemData}
        onClose={closeModal}
        isLoading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
        onSubmit={handleSubmit}
      />
      <AppDialog
        open={appDialogState.open}
        onClose={appDialogState.onClose}
        content={appDialogState.content}
        title={appDialogState.title}
        firstBtnContent={appDialogState.firstBtnText}
        secondBtnContent={
          deleteCategoryMutation?.isPending
            ? "Please wait..." : appDialogState.secondBtnText
        }
        firstBtnClickHandler={appDialogState.onClose}
        secondBtnClickHandler={appDialogState.onConfirm}
        options={{ btnLoading: deleteCategoryMutation?.isPending }}
      />
    </Container>
  );
};

export default Categories;
